import React, { useEffect, useState } from 'react';
import { getApiBase } from '../utils/api';
import LandingPage from './dashboard/LandingPage';
import LandingPageV2 from './dashboard/LandingPageV2';
import LandingPageV3 from './dashboard/LandingPageV3';

/**
 * Public landing page wrapper.
 *
 * Looks up the currently published version from the backend
 * (superadmin-controlled via /admin/landing-config) and renders it.
 *
 * Exposed on the public /welcome route so prospective beta clients can
 * view the page without needing to sign in.
 */
const PublicLanding = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${getApiBase()}/landing/config`, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
        });
        if (res.ok) {
          setConfig(await res.json());
        } else {
          setError('Landing page is not available right now.');
        }
      } catch (err) {
        console.error('Failed to load landing config:', err);
        setError('Landing page is not available right now.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Coming Soon</h1>
          <p className="text-slate-600">
            {error || 'This page is not yet available. Please check back soon.'}
          </p>
        </div>
      </div>
    );
  }

  if (!config.published) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Coming Soon</h1>
          <p className="text-slate-600">
            We're putting the finishing touches on something new. Please check back soon.
          </p>
        </div>
      </div>
    );
  }

  switch (config.version) {
    case 'v1':
      return <LandingPage publicMode />;
    case 'v2':
      return <LandingPageV2 publicMode />;
    case 'v3':
    default:
      return <LandingPageV3 publicMode />;
  }
};

export default PublicLanding;
